import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, Trophy, Ship, Loader2, XCircle, ArrowLeft, MessageCircle } from "lucide-react";
import axios from "axios";
import "@/App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "5547997920909";
const MAX_POLL_ATTEMPTS = 10;
const POLL_INTERVAL = 2000;

function Sucesso() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [attempts, setAttempts] = useState(0);
  
  const pollPaymentStatus = useCallback(async (currentAttempt) => {
    if (currentAttempt >= MAX_POLL_ATTEMPTS) {
      setError("Tempo limite excedido. Verifique seu email para confirmação.");
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      const data = response.data;
      
      if (data.payment_status === "paid") {
        setStatus(data);
        setLoading(false);
        return;
      } else if (data.status === "expired") {
        setError("Sessão de pagamento expirada.");
        setLoading(false);
        return;
      }
      
      // Continue polling
      setAttempts(currentAttempt + 1);
      setTimeout(() => pollPaymentStatus(currentAttempt + 1), POLL_INTERVAL);
      
    } catch (err) {
      // Non-critical: continue polling, payment might still be processing
      if (process.env.NODE_ENV === 'development') {
        console.warn("Payment status check failed:", err.message);
      }
      setAttempts(currentAttempt + 1);
      setTimeout(() => pollPaymentStatus(currentAttempt + 1), POLL_INTERVAL);
    }
  }, [sessionId]);
  
  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(0);
    } else {
      setError("Sessão não encontrada");
      setLoading(false);
    }
  }, [sessionId, pollPaymentStatus]);
  
  const getPalpiteLabel = (palpite) => {
    return palpite === "brasil_ganha" ? "Brasil Ganha! 🏆" : "Brasil Perde 😅";
  };
  
  const openWhatsApp = () => {
    const message = encodeURIComponent(
      `Oi! Acabei de garantir meu voucher do Desafio do Hexa! Meu palpite: ${getPalpiteLabel(status?.palpite)}. Session: ${sessionId}`
    );
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#002776] to-[#001a4d] flex items-center justify-center">
        <div className="text-center text-white">
          <Loader2 className="w-16 h-16 animate-spin mx-auto mb-4 text-[#FFDF00]" />
          <h2 className="font-heading text-2xl mb-2">Processando pagamento...</h2>
          <p className="text-white/70">Aguarde enquanto confirmamos seu pagamento</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#002776] to-[#001a4d] flex items-center justify-center px-4">
        <div className="text-center text-white max-w-md">
          <XCircle className="w-16 h-16 mx-auto mb-4 text-red-400" />
          <h2 className="font-heading text-2xl mb-2">Ops! Algo deu errado</h2>
          <p className="text-white/70 mb-6">{error}</p>
          <Link 
            to="/"
            className="inline-flex items-center gap-2 bg-[#FFDF00] text-[#002776] font-bold py-3 px-6 rounded-full hover:bg-[#e6c900] transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Voltar para o início
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#002776] via-[#001a4d] to-[#002776]">
      {/* Success Content */}
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        {/* Success Icon */}
        <div className="w-24 h-24 rounded-full bg-[#009C3B] flex items-center justify-center mx-auto mb-6 animate-bounce-soft">
          <CheckCircle className="w-14 h-14 text-white" />
        </div>
        
        <h1 className="font-heading text-4xl md:text-5xl text-white mb-4">
          PARABÉNS! 🎉
        </h1>
        
        <p className="text-xl text-white/90 mb-8">
          Seu voucher do <span className="text-[#FFDF00] font-bold">Desafio do Hexa</span> foi garantido!
        </p>
        
        {/* Palpite Card */}
        <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 mb-8 border border-white/20">
          <p className="text-white/70 text-sm mb-3">Seu palpite para a Copa:</p>
          
          <div className={`inline-flex items-center gap-3 px-6 py-4 rounded-2xl ${
            status?.palpite === "brasil_ganha" 
              ? "bg-[#009C3B]" 
              : "bg-white/20"
          }`}>
            {status?.palpite === "brasil_ganha" ? (
              <Trophy className="w-8 h-8 text-[#FFDF00]" />
            ) : (
              <Ship className="w-8 h-8 text-white" />
            )}
            <span className="font-heading text-2xl text-white">
              {getPalpiteLabel(status?.palpite)}
            </span>
          </div>
          
          <p className="text-white/70 text-sm mt-4">
            Se você <span className="text-[#FFDF00] font-bold">acertar</span>, ganha:
          </p>
          <ul className="text-white/90 text-sm mt-2 space-y-1">
            <li>✓ 100% do valor em crédito, OU</li>
            <li>✓ Um segundo passeio de graça, OU</li>
            <li>✓ Trazer +2 amigos</li>
          </ul>
        </div>
        
        {/* Info Cards */}
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
            <p className="text-white/70 text-sm mb-1">Valor pago</p>
            <p className="font-heading text-3xl text-[#FFDF00]">R$247</p>
            <p className="text-white/70 text-xs">para 2 pessoas</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
            <p className="text-white/70 text-sm mb-1">Validade</p>
            <p className="font-heading text-3xl text-white">12 meses</p>
            <p className="text-white/70 text-xs">a partir da compra</p>
          </div>
        </div>
        
        {/* Next Steps */}
        <div className="bg-[#009C3B]/20 rounded-2xl p-6 mb-8 border border-[#009C3B]/30">
          <h3 className="font-heading text-xl text-white mb-4">📋 Próximos Passos</h3>
          <ol className="text-left text-white/90 space-y-3">
            <li className="flex items-start gap-3">
              <span className="bg-[#FFDF00] text-[#002776] w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
              <span>Você receberá um email com a confirmação e detalhes do voucher</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="bg-[#FFDF00] text-[#002776] w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
              <span>Entre em contato pelo WhatsApp para agendar sua data</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="bg-[#FFDF00] text-[#002776] w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
              <span>Aproveite o passeio e torça pelo Brasil! 🇧🇷</span>
            </li>
          </ol>
        </div>
        
        {/* CTA Buttons */}
        <div className="flex flex-col gap-4">
          <button
            onClick={openWhatsApp}
            className="bg-[#25D366] hover:bg-[#20BD5A] text-white font-bold py-4 px-8 rounded-full text-lg flex items-center justify-center gap-2 mx-auto transition-colors"
          >
            <MessageCircle className="w-6 h-6" />
            AGENDAR PELO WHATSAPP
          </button>
          
          <Link 
            to="/"
            className="text-white/70 hover:text-white text-sm underline transition-colors"
          >
            Voltar para o site
          </Link>
        </div>
        
        {/* Session ID */}
        <p className="text-white/40 text-xs mt-8">
          ID da transação: {sessionId}
        </p>
      </div>
    </div>
  );
}

export default Sucesso;
